import { useEffect, useState, useRef } from 'react';
import {
  MagnifyingGlass, Plus, X, Spinner, CaretUp, CaretDown,
  Image as ImageIcon, Bone, SortAscending, SortDescending,
  ArrowsOutSimple, Tag as TagIcon,
} from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useAssetStore } from '../../stores/assetStore';
import { useAuthStore } from '../../stores/authStore';
import { useSceneStore } from '../../stores/sceneStore';
import FolderTree from './FolderTree';
import SmartAssetImporter from './SmartAssetImporter';
import DockedAssetRow from './DockedAssetRow';
import AssetLibraryDialog from './AssetLibraryDialog';
import ReviewQueue from './ReviewQueue';
import DriveBrowser from './DriveBrowser';
import { useAssetPanelPrefs } from './useAssetPanelPrefs';
import { sortAssets, groupAssetsByType } from './assetSortUtils';
import type { SortField } from './assetSortUtils';
import type { Asset } from '@spine-workspace/shared';

interface AssetLibraryProps {
  onDragAsset?: (asset: Asset) => void;
  onDoubleClickAsset?: (asset: Asset) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  viewOnly?: boolean;
}

const SORT_FIELDS: { field: SortField; label: string }[] = [
  { field: 'name', label: 'Name' },
  { field: 'type', label: 'Type' },
  { field: 'date', label: 'Date' },
];

export default function AssetLibrary({
  onDragAsset,
  onDoubleClickAsset,
  isCollapsed,
  onToggleCollapse,
  viewOnly: _viewOnly,
}: AssetLibraryProps) {
  const {
    loading, searchQuery, setSearchQuery, fetchAssets, fetchFolders, fetchTags,
    filteredAssets, selectedAssetIds, selectionAnchorId,
    selectAsset, selectAssets, tags, selectedTags, toggleTag,
    uploadImageAsset, fetchSubmissions,
  } = useAssetStore();
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false);

  const [activeTab, setActiveTab] = useState<'assets' | 'drive'>('assets');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showLibraryDialog, setShowLibraryDialog] = useState(false);
  const [showImportMenu, setShowImportMenu] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const importMenuRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const assetListRef = useRef<HTMLDivElement>(null);

  const { prefs, update: updatePrefs } = useAssetPanelPrefs();

  useEffect(() => {
    fetchAssets();
    fetchFolders();
    fetchTags();
    if (isAdmin) fetchSubmissions();
  }, [fetchAssets, fetchFolders, fetchTags, fetchSubmissions, isAdmin]);

  useEffect(() => {
    if (!showImportMenu) return;
    function onOutside(e: MouseEvent) {
      if (importMenuRef.current && !importMenuRef.current.contains(e.target as Node)) {
        setShowImportMenu(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [showImportMenu]);

  const raw = filteredAssets();
  const sorted = sortAssets(raw, prefs.sortField, prefs.sortDir);
  const groups = prefs.groupByType ? groupAssetsByType(sorted) : [{ label: '', items: sorted }];

  // Build a quick lookup map for drag serialization
  const assetMap = Object.fromEntries(sorted.map((a) => [a.id, a]));

  function toggleSort(field: SortField) {
    if (prefs.sortField === field) {
      updatePrefs({ sortDir: prefs.sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      updatePrefs({ sortField: field, sortDir: 'asc' });
    }
  }

  function handleAssetClick(asset: Asset, e: React.MouseEvent) {
    const allIds = sorted.map((a) => a.id);

    if (e.shiftKey && selectionAnchorId) {
      const anchorIdx = allIds.indexOf(selectionAnchorId);
      const clickIdx = allIds.indexOf(asset.id);
      if (anchorIdx >= 0 && clickIdx >= 0) {
        const start = Math.min(anchorIdx, clickIdx);
        const end = Math.max(anchorIdx, clickIdx);
        selectAssets(allIds.slice(start, end + 1));
      } else {
        selectAsset(asset.id);
      }
    } else if (e.ctrlKey || e.metaKey) {
      selectAsset(asset.id, { multi: true });
    } else {
      selectAsset(asset.id);
    }
    useSceneStore.getState().selectLayer(null);
  }

  function handleDoubleClick(asset: Asset) {
    if (onDoubleClickAsset) {
      // Add all selected assets when double-clicking one of them
      const { selectedAssetIds: ids, assets } = useAssetStore.getState();
      if (ids.length > 1 && ids.includes(asset.id)) {
        const assetObjs = ids.map((id) => assets.find((a) => a.id === id)).filter(Boolean) as Asset[];
        assetObjs.forEach((a) => onDoubleClickAsset(a));
      } else {
        onDoubleClickAsset(asset);
      }
    }
  }

  function handleDragStart(e: React.DragEvent, asset: Asset) {
    const { selectedAssetIds: ids } = useAssetStore.getState();
    const dragAssets = ids.includes(asset.id) && ids.length > 1
      ? ids.map((id) => assetMap[id]).filter(Boolean)
      : [asset];
    e.dataTransfer.setData('application/spine-asset', JSON.stringify(dragAssets));
    e.dataTransfer.effectAllowed = 'copy';
    onDragAsset?.(asset);
  }

  return (
    <div className="flex flex-col h-full bg-bg-surface">
      {/* Section header — always visible */}
      <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between shrink-0">
        <span className="font-serif text-base font-medium text-accent tracking-[0.01em]">Assets</span>
        <div className="flex items-center gap-1">
          {!isCollapsed && (
            <>
              {/* Open dialog button */}
              <button
                title="Open Asset Explorer"
                data-testid="open-asset-dialog-btn"
                onClick={() => setShowLibraryDialog(true)}
                className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-bg-hover transition-all duration-fast"
              >
                <ArrowsOutSimple size={13} />
              </button>

              {/* Import menu */}
              <div className="relative" ref={importMenuRef}>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  multiple
                  className="hidden"
                  data-testid="image-upload-input"
                  onChange={async (e) => {
                    const files = Array.from(e.target.files ?? []);
                    if (!files.length) return;
                    setImageUploading(true);
                    try {
                      await Promise.all(files.map((f) => uploadImageAsset(f)));
                    } finally {
                      setImageUploading(false);
                      if (imageInputRef.current) imageInputRef.current.value = '';
                    }
                  }}
                />
                <button
                  title="Import asset"
                  data-testid="import-asset-btn"
                  onClick={() => setShowImportMenu((v) => !v)}
                  className={`w-6 h-6 flex items-center justify-center rounded transition-all duration-fast ${
                    imageUploading
                      ? 'text-accent animate-pulse'
                      : showImportMenu
                        ? 'text-accent bg-accent-muted'
                        : 'text-text-secondary hover:text-text-primary hover:bg-bg-hover'
                  }`}
                >
                  <Plus size={14} />
                </button>

                {showImportMenu && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-bg-elevated border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
                    <button
                      data-testid="import-spine-btn"
                      onClick={() => { setShowImportMenu(false); setShowImportDialog(true); }}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
                    >
                      <Bone size={13} className="text-accent/70 shrink-0" />
                      Spine Assets
                    </button>
                    <button
                      data-testid="import-image-btn"
                      onClick={() => { setShowImportMenu(false); imageInputRef.current?.click(); }}
                      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs font-sans text-text-secondary hover:text-text-primary hover:bg-bg-hover transition-colors duration-fast"
                    >
                      <ImageIcon size={13} className="text-emerald-400/70 shrink-0" />
                      Image
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="w-5 h-5 flex items-center justify-center rounded text-text-tertiary hover:text-accent hover:bg-bg-hover transition-all duration-fast"
              title={isCollapsed ? 'Expand Assets' : 'Collapse Assets'}
              data-testid="assets-collapse-btn"
            >
              {isCollapsed
                ? <CaretDown size={10} weight="bold" />
                : <CaretUp size={10} weight="bold" />
              }
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-border-subtle shrink-0">
        {(['assets', 'drive'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-1.5 text-[10px] font-mono uppercase tracking-wide transition-colors ${
              activeTab === tab
                ? 'text-accent border-b-2 border-accent'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            {tab === 'assets' ? 'My Assets' : 'Drive'}
          </button>
        ))}
      </div>

      {/* Drive tab */}
      {activeTab === 'drive' && (
        <div className="flex-1 min-h-0 overflow-hidden">
          <DriveBrowser onDragAsset={onDragAsset} onDoubleClickAsset={onDoubleClickAsset} />
        </div>
      )}

      {activeTab === 'assets' && (
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Review queue — admin only */}
        {isAdmin && <ReviewQueue onOpenAsset={onDoubleClickAsset} />}

        {/* Search */}
        <div className="px-3 py-2 border-b border-border-subtle shrink-0">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
            <input
              className="w-full bg-bg-input border border-border text-text-primary rounded-md
                         pl-8 pr-3 py-[7px] text-sm font-sans placeholder:text-text-tertiary
                         hover:bg-bg-hover focus:outline-none focus:border-accent focus:shadow-focus
                         transition-all duration-normal"
              placeholder="Search assets…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Sort bar */}
        <div className="px-2 py-1 border-b border-border-subtle shrink-0 flex items-center gap-0.5">
          {SORT_FIELDS.map(({ field, label }) => (
            <button
              key={field}
              onClick={() => toggleSort(field)}
              className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                prefs.sortField === field
                  ? 'bg-accent-muted text-accent'
                  : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              {label}
              {prefs.sortField === field && (
                prefs.sortDir === 'asc'
                  ? <SortAscending size={8} />
                  : <SortDescending size={8} />
              )}
            </button>
          ))}

          <button
            onClick={() => updatePrefs({ groupByType: !prefs.groupByType })}
            title={prefs.groupByType ? 'Ungroup' : 'Group by type'}
            className={`ml-auto px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
              prefs.groupByType
                ? 'bg-accent-muted text-accent'
                : 'text-text-tertiary hover:text-text-primary hover:bg-bg-hover'
            }`}
          >
            Group
          </button>
        </div>

        {/* Tag filter pills */}
        {tags.length > 0 && (
          <div className="px-2 py-1 border-b border-border-subtle shrink-0 flex items-center gap-1 flex-wrap">
            <TagIcon size={10} className="text-text-tertiary shrink-0" />
            {tags.map((tag) => {
              const active = selectedTags.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={`px-1.5 py-px rounded text-[9px] font-mono transition-all ${
                    active ? 'text-white' : 'text-text-secondary hover:text-text-primary bg-bg-hover'
                  }`}
                  style={active ? { backgroundColor: tag.color } : undefined}
                >
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}

        {/* Folder tree */}
        <div className="px-2 pt-1.5 shrink-0">
          <FolderTree />
        </div>

        {/* Asset list */}
        <div
          ref={assetListRef}
          data-nav-panel="assets"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            if (sorted.length === 0) return;
            e.preventDefault();
            const { selectedAssetIds: ids, selectionAnchorId: anchor } = useAssetStore.getState();
            const allIds = sorted.map((a) => a.id);
            // Navigate from the last selected item (or anchor for shift-nav)
            const lastId = ids[ids.length - 1] ?? anchor;
            const currentIdx = allIds.indexOf(lastId ?? '');
            const nextIdx = e.key === 'ArrowDown'
              ? Math.min(currentIdx < 0 ? 0 : currentIdx + 1, allIds.length - 1)
              : Math.max(currentIdx < 0 ? 0 : currentIdx - 1, 0);
            const nextId = allIds[nextIdx];
            if (!nextId) return;

            if (e.shiftKey && anchor) {
              // Extend range from anchor to nextIdx
              const anchorIdx = allIds.indexOf(anchor);
              if (anchorIdx >= 0) {
                const start = Math.min(anchorIdx, nextIdx);
                const end = Math.max(anchorIdx, nextIdx);
                selectAssets(allIds.slice(start, end + 1));
              }
            } else {
              selectAsset(nextId);
              useSceneStore.getState().selectLayer(null);
            }
            setTimeout(() => {
              assetListRef.current?.querySelector(`[data-testid="asset-row-${nextId}"]`)?.scrollIntoView({ block: 'nearest' });
            }, 0);
          }}
          className="flex-1 overflow-y-auto pb-2 outline-none"
        >
          {loading ? (
            <div className="flex items-center justify-center py-8 text-text-tertiary">
              <Spinner size={16} className="animate-spin" />
            </div>
          ) : sorted.length === 0 ? (
            <p className="text-text-tertiary text-xs text-center py-6 px-4 font-sans">
              {searchQuery ? 'No matching assets' : 'No assets yet — import below'}
            </p>
          ) : (
            <div className="py-1">
              {groups.map((group) => (
                <div key={group.label}>
                  {group.label && (
                    <p className="px-3 pt-2 pb-0.5 text-[8px] font-mono font-semibold text-text-tertiary uppercase tracking-wide">
                      {group.label}
                    </p>
                  )}
                  {group.items.map((asset) => (
                    <DockedAssetRow
                      key={asset.id}
                      asset={asset}
                      isSelected={selectedAssetIds.length === 1 && selectedAssetIds[0] === asset.id}
                      isMultiSelected={selectedAssetIds.length > 1 && selectedAssetIds.includes(asset.id)}
                      onClick={handleAssetClick}
                      onDoubleClick={handleDoubleClick}
                      onDragStart={handleDragStart}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Smart import dialog */}
      <Dialog.Root open={showImportDialog} onOpenChange={(o) => { if (!o) setShowImportDialog(false); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/75 z-40" />
          <Dialog.Content
            data-testid="smart-import-dialog"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                       w-[min(480px,calc(100vw-32px))]
                       bg-bg-elevated border border-border rounded-xl shadow-lg
                       focus:outline-none flex flex-col max-h-[80vh]"
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border-subtle shrink-0">
              <div>
                <Dialog.Title className="font-serif text-md font-medium text-text-primary tracking-[0.01em]">
                  Import Spine Assets
                </Dialog.Title>
                <p className="text-xxs text-text-tertiary font-sans mt-0.5">
                  Drop files, a folder, or browse — Spine groups detected automatically
                </p>
              </div>
              <Dialog.Close className="w-6 h-6 flex items-center justify-center rounded text-text-tertiary hover:text-text-primary hover:bg-bg-hover transition-all duration-fast shrink-0 ml-3">
                <X size={13} />
              </Dialog.Close>
            </div>
            <div className="overflow-y-auto min-h-0 bg-bg-elevated">
              <SmartAssetImporter onImportComplete={() => setShowImportDialog(false)} />
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Asset Library Dialog (explorer mode) */}
      <AssetLibraryDialog
        open={showLibraryDialog}
        onClose={() => setShowLibraryDialog(false)}
        prefs={prefs}
        onPrefsChange={updatePrefs}
        onDoubleClickAsset={onDoubleClickAsset}
      />
    </div>
  );
}
